import { readStatesDataCache } from './statesDataCache';
import { stateOptions } from './stateOptions';

interface CachedMarket {
  id: string;
  name: string;
}

interface CachedState {
  name: string; // full state name (e.g. "Texas"), as returned by getMarketList
  markets: CachedMarket[];
}

const toAbbreviation = (stateName: string): string | null => {
  if (!stateName) return null;
  if (stateName.length === 2) return stateName.toUpperCase();
  const option = stateOptions.find(
    (opt) => opt.label.toLowerCase() === stateName.toLowerCase()
  );
  return option ? option.value : null;
};

/**
 * A plan has no direct "states" field from the API today - only marketIds.
 * Since every market belongs to exactly one state (statesData, cached from
 * getMarketList), a plan's states can be derived by resolving each market id
 * to its parent state and converting that state's full name to its
 * 2-letter abbreviation (the form add_plan_to_states/remove_plan_from_states
 * and every other state-keyed field in this app use).
 *
 * Callers that already hold freshly-fetched states in React state (e.g.
 * PlansList) should pass it as `statesData` so this stays in sync with that
 * state instead of whatever was last written to localStorage - reading
 * localStorage directly caused a stale/empty-cache race where plans always
 * resolved to zero states on first load.
 */
export const getStatesForMarketIds = (
  marketIds: string[] | null | undefined,
  statesDataOverride?: CachedState[]
): string[] => {
  if (!marketIds || marketIds.length === 0) return [];

  const statesData: CachedState[] =
    statesDataOverride ?? readStatesDataCache<CachedState>() ?? [];

  const marketIdToStateName = new Map<string, string>();
  for (const state of statesData) {
    if (!Array.isArray(state.markets)) continue;
    for (const market of state.markets) {
      if (market?.id) {
        marketIdToStateName.set(market.id, state.name);
      }
    }
  }

  const abbreviations = new Set<string>();
  for (const marketId of marketIds) {
    const stateName = marketIdToStateName.get(marketId);
    if (!stateName) continue;
    const abbreviation = toAbbreviation(stateName);
    if (abbreviation) {
      abbreviations.add(abbreviation);
    }
  }

  return Array.from(abbreviations).sort();
};
